import { CrashConfig } from "./crash_config.js";
import { NativeBridge } from "./native_bridge.js";
import type { CrashEvent } from "./types.js";

const QUEUE_KEY = 'bb_crash_pending_v1';

export class CrashTransport
{
  private static flushing = false;

  public static async send( event: CrashEvent ): Promise<void>
  {
    if ( NativeBridge.isAvailable() )
    {
      const stored = await NativeBridge.capture( event );
      if ( stored ) return;
    }
    this.enqueue( event );
    void this.flush();
  }

  public static async flush(): Promise<{ sent: number; pending: number }>
  {
    if ( NativeBridge.isAvailable() )
    {
      const native = await NativeBridge.flush();
      return native ?? { sent: 0, pending: 0 };
    }

    const endpoint = CrashConfig.webEndpoint();
    const key = CrashConfig.webKey();
    const queue = this.readQueue();
    if ( this.flushing || queue.length === 0 || !endpoint || !key || !navigator.onLine )
      return { sent: 0, pending: queue.length };

    this.flushing = true;
    let sent = 0;
    try
    {
      const batch = queue.slice( 0, CrashConfig.WEB_QUEUE_LIMIT );
      const controller = new AbortController();
      const timer = setTimeout( () => controller.abort(), CrashConfig.WEB_SEND_TIMEOUT_MS );
      try
      {
        const res = await fetch( `${endpoint}/api/v1/events`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Crashlytics-Key': key },
          body: JSON.stringify( batch ),
          keepalive: true,
          signal: controller.signal,
        } );
        if ( res.ok || ( res.status >= 400 && res.status < 500 && res.status !== 429 ) )
        {
          const ids = new Set( batch.map( e => e.event_id ) );
          this.writeQueue( queue.filter( e => !ids.has( e.event_id ) ) );
          sent = res.ok ? batch.length : 0;
          await logRejected( res, batch.length );
        }
      }
      finally
      {
        clearTimeout( timer );
      }
    }
    catch ( e )
    {
      console.warn( '[Crash] web flush failed', e );
    }
    finally
    {
      this.flushing = false;
    }
    return { sent, pending: this.readQueue().length };
  }

  public static pendingCount(): number
  {
    return this.readQueue().length;
  }

  private static enqueue( event: CrashEvent ): void
  {
    const queue = this.readQueue();
    queue.push( event );
    while ( queue.length > CrashConfig.WEB_QUEUE_LIMIT ) queue.shift();
    this.writeQueue( queue );
  }

  private static memory: CrashEvent[] | null = null;

  private static readQueue(): CrashEvent[]
  {
    if ( this.memory ) return this.memory.slice();
    let parsed: unknown = [];
    try
    {
      const raw = localStorage.getItem( QUEUE_KEY );
      parsed = raw ? JSON.parse( raw ) : [];
    }
    catch {}
    this.memory = Array.isArray( parsed ) ? parsed as CrashEvent[] : [];
    return this.memory.slice();
  }

  private static writeQueue( queue: CrashEvent[] ): void
  {
    this.memory = queue.slice();
    try
    {
      if ( queue.length === 0 ) localStorage.removeItem( QUEUE_KEY );
      else localStorage.setItem( QUEUE_KEY, JSON.stringify( queue ) );
    }
    catch {}
  }
}

async function logRejected( res: Response, sent: number ): Promise<void>
{
  try
  {
    const body = await res.json() as { error?: string; rejected?: Array<{ reason?: string }> };
    if ( !res.ok ) { console.warn( `[Crash] server answered HTTP ${res.status}: ${body.error ?? ''}` ); return; }
    const rejected = body.rejected ?? [];
    if ( rejected.length === 0 ) return;
    const reasons = new Map<string, number>();
    for ( const r of rejected ) reasons.set( r.reason ?? 'unknown', ( reasons.get( r.reason ?? 'unknown' ) ?? 0 ) + 1 );
    console.warn( `[Crash] server rejected ${rejected.length} of ${sent} event(s): ${[ ...reasons ].map( ( [ reason, n ] ) => `${reason} ×${n}` ).join( ', ' )}` );
  }
  catch {}
}
